// IssueRegisterTable — the left-column issue register. One row per issue:
// title + issueRef (mono) + owner name + severity dot + status pill +
// duration-in-state tag + age column. Closed and resolved rows are demoted
// (reduced opacity) so the live register reads first. The selected row gets
// a SLATE left-border (not violet — the tab's three violet slots are the
// active filter chip, the composer Post button, and the drawer Update-status
// icon). Rows are buttons in a listbox so keyboard + screen-reader users can
// move and select with arrow keys.
//
// Owner ids resolve to display names via the shared useUserDisplays batch
// lookup. Age + duration-in-state are derived on read via deriveIssueDisplay
// so the register can never drift from the drawer.
//
// The empty register has two distinct states: a filter miss ("no issues match
// the current filters") and a never-populated register ("no issues raised",
// with a primary action to raise the first one). The caller passes
// hasActiveFilters to disambiguate.

import { useMemo } from "react";
import { Plus } from "lucide-react";
import { Button } from "../../../common";
import { useUserDisplays } from "../../../../hooks/useUsers";
import TrackingStatusBadge from "../shared/TrackingStatusBadge";
import IssueSeverity from "./IssueSeverity";
import IssueDurationTag from "./IssueDurationTag";
import { deriveIssueDisplay } from "./deriveIssueDisplay";
import { getBrisbaneToday } from "../../../../utils/getBrisbaneToday";
import { toBrisbaneDay } from "../../../../utils/toBrisbaneDay";
import type { Issue } from "../../../../types";

export interface IssueRegisterTableProps {
  issues: Issue[];
  selectedId: string | null;
  onSelect: (issueId: string) => void;
  today?: Date;
  /** True when any chip filter or the search box is active. Distinguishes a
   *  filter miss ("no issues match") from a genuinely empty register ("no
   *  issues raised yet"). Defaults to false. */
  hasActiveFilters?: boolean;
  /** When the register is genuinely empty and the user can write, offer a
   *  primary action to raise the first issue. */
  canRaise?: boolean;
  onRaiseFirst?: () => void;
}

function startOfUTCDay(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function toIsoDay(d: Date): string {
  return new Date(startOfUTCDay(d)).toISOString().slice(0, 10);
}

/** Closed and resolved issues are filed away / provisionally done, so their
 *  rows recede behind the live register. */
function isDemoted(issue: Issue): boolean {
  return issue.status === "closed" || issue.status === "resolved";
}

export default function IssueRegisterTable({
  issues,
  selectedId,
  onSelect,
  today,
  hasActiveFilters = false,
  canRaise = false,
  onRaiseFirst,
}: IssueRegisterTableProps) {
  // Age + duration anchor to the Brisbane calendar day (matching the backend +
  // parity fixtures), not the browser wall-clock. Tests inject a fixed Date.
  const todayIso = today ? toIsoDay(today) : getBrisbaneToday();

  const ownerIds = useMemo(
    () =>
      Array.from(
        new Set(
          issues
            .map((i) => i.ownerId)
            .filter((id): id is string => !!id),
        ),
      ),
    [issues],
  );
  const { data: owners } = useUserDisplays(ownerIds);

  if (issues.length === 0) {
    // A filter miss and a never-populated register read very differently: the
    // first tells the user to relax their filters, the second invites them to
    // raise the first issue. Conflating them left a brand-new contract showing
    // "no issues match the current filters" with no filters set.
    if (hasActiveFilters) {
      return (
        <div
          data-testid="issue-register-empty"
          className="rounded-lg border border-slate-200 bg-white p-8 text-center text-body-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400"
        >
          No issues match the current filters.
        </div>
      );
    }
    return (
      <div
        data-testid="issue-register-empty-unfiltered"
        className="rounded-lg border border-slate-200 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-800/60"
      >
        <p className="text-body font-semibold text-slate-700 dark:text-slate-200">
          No issues raised
        </p>
        <p className="mt-1 text-body-sm text-slate-500 dark:text-slate-400">
          Log problems, breaches, or disputes against this contract.
        </p>
        {canRaise && onRaiseFirst && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={onRaiseFirst}
            >
              Raise first issue
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <ul
      role="listbox"
      aria-label="Issues"
      className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200 bg-white dark:divide-slate-700/60 dark:border-slate-700 dark:bg-slate-800/40"
    >
      {issues.map((issue) => {
        const selected = issue.id === selectedId;
        const demoted = isDemoted(issue);
        // raisedAt / statusEnteredAt are full ISO instants from the backend;
        // the derivation expects Brisbane calendar days, so normalise first.
        const { issueAgeDays, durationInStateDays } = deriveIssueDisplay({
          today: todayIso,
          raisedAt: toBrisbaneDay(issue.raisedAt),
          statusEnteredAt: toBrisbaneDay(issue.statusEnteredAt),
        });
        const ownerName = issue.ownerId
          ? (owners.get(issue.ownerId)?.name ?? issue.ownerId)
          : "Unassigned";
        return (
          <li key={issue.id} role="none">
            <button
              type="button"
              role="option"
              aria-selected={selected}
              data-testid={`issue-row-${issue.id}`}
              onClick={() => onSelect(issue.id)}
              className={[
                "flex w-full items-start gap-3 px-4 py-3 text-left transition-colors",
                "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-400",
                "hover:bg-slate-50 dark:hover:bg-slate-700/40",
                selected
                  ? "border-l-2 border-l-slate-500 bg-slate-50 dark:border-l-slate-300 dark:bg-slate-700/40"
                  : "border-l-2 border-l-transparent",
                demoted ? "opacity-60" : "",
              ].join(" ")}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-body-sm font-semibold text-slate-900 dark:text-slate-100">
                  {issue.title}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-caption text-slate-500 dark:text-slate-400">
                  <span className="font-mono">{issue.issueRef}</span>
                  <span aria-hidden="true">·</span>
                  <span className="truncate">{ownerName}</span>
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1.5">
                <IssueSeverity severity={issue.severity} />
                {/* Status pill + its time-in-state caption stacked together so
                    the duration reads as "how long in the CURRENT status" and
                    groups with the pill — distinct from the Age column below.
                    The labelled caption is shown ONLY when time-in-state
                    differs from total age; when they coincide (open, or any
                    state entered at raise) it's suppressed so the two counts
                    never read as the same number twice. */}
                <div className="flex flex-col items-end gap-0.5">
                  <TrackingStatusBadge kind="issue" status={issue.status} />
                  {durationInStateDays !== issueAgeDays && (
                    <IssueDurationTag
                      status={issue.status}
                      durationDays={durationInStateDays}
                    />
                  )}
                </div>
                {/* Age = total since raised (a DISTINCT axis from time-in-
                    state). Labelled + tooltipped so the two counts never read
                    as duplicate floating numbers. */}
                <span
                  className="tabular-nums text-caption text-slate-500 dark:text-slate-400"
                  title={`Raised ${issueAgeDays} ${issueAgeDays === 1 ? "day" : "days"} ago`}
                >
                  {/* slate-500 (not slate-400) for WCAG AA — this 12px label
                      sits on the white row and needs ≥4.5:1. */}
                  <span className="text-slate-500 dark:text-slate-400">
                    Age
                  </span>{" "}
                  {issueAgeDays}d
                </span>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
